// 應答風格 ↔ 底層引擎 對應（UI 不出現引擎名）
//   精準應答(standard) = Gemini：查完再答、無過渡語（預設）
//   即時互動(instant)  = OpenAI：先回應、邊查邊講、不冷場
//
// provider 存 avatar.ivrFlowConfig.liveConfig.provider（不設 = gemini）。
// 音色：Gemini 存 defaultVoiceId；OpenAI / Grok 存 liveConfig.voice。
//
// 🔴 2026-08-08：「即時互動」底層從 Grok 換成 OpenAI。
//    Grok 實測三個擋路問題：把 prompt 當台詞唸給客人聽（詞彙黑名單只能降機率、
//    每次被擋就換一種說法）、送話斷續（outStarves 2~5，Gemini 同題目 0~1）、
//    以及「不洩漏」與「業務正確」在 prompt 長短上互斥。
//    Grok 不再開放新選，但既有 avatar 仍要能正常顯示與編輯 → 相關函式保留相容。

export const RESPONSE_STYLES = {
    standard: { provider: 'gemini' },   // 預設
    instant: { provider: 'openai' },    // 0808 起：原本是 grok
    // 0914：即時互動 2.0 = OpenAI gpt-live-1（full-duplex 嘴巴）+ Gemini 腦袋（KB 走 3.5-flash-lite、收單/API 走 3.8-flash）
    //   見 ai_backend/module/AgentFlow/Live/adapters/GptLiveAdapter.js。AA(transfer-only) 不開放（失去「聽聲音對通訊錄」能力）。
    // 0920 Felix：「即時互動 2.0」底層換成 Qwen（qwen3.8-omni-flash-realtime 腦袋 + Fish Audio s2.1-pro 複刻聲嘴巴），
    //   UI 文案不提 Qwen。voice 存 Fish model id；提示詞走 LiveNodeConfigBuilder 的 qwen 精簡模板
    //   （見 ai_backend/module/AgentFlow/Live/adapters/QwenRealtimeTtsAdapter.js）。
    //   舊的 openai-live（gpt-live-1 嘴巴 + Gemini 腦袋）既有角色仍顯示為即時互動 2.0，不再開放新選。
    //   「即時互動」(openai) 已從新建卡片拿掉，既有角色照舊。
    live: { provider: 'qwen-omni-tts' },
};

// provider → 應答風格 key（工作室讀 provider 反查）
// grok 是歷史值，仍歸在「即時互動」，否則既有 grok avatar 會被顯示成精準應答。
export function providerToStyle(provider) {
    const p = (provider || 'gemini').toLowerCase();
    if (p === 'openai-live' || p === 'qwen-omni-tts' || p === 'qwen') return 'live';
    return (p === 'openai' || p === 'grok') ? 'instant' : 'standard';
}

// OpenAI Realtime 內建音色（10 個，realtime 與 TTS 共用同一組 id → 開場白與通話音色一致）。
// marin / cedar 是新旗艦音色。預設 shimmer。
export const OPENAI_VOICES = {
    female: ['shimmer', 'coral', 'sage', 'marin', 'alloy'],
    male: ['cedar', 'echo', 'ash', 'ballad', 'verse'],
};
export const OPENAI_DEFAULT_VOICE = 'shimmer';

// gpt-live-1（即時互動 2.0）音色：0911 staging 實測 session.start 都接受。marin 最穩、coral 較年輕但音量小一半。
export const OPENAI_LIVE_VOICES = {
    female: ['marin', 'coral', 'gleam', 'quartz', 'willow'],
    male: ['cedar', 'meridian', 'vesper', 'stone'],
};
export const OPENAI_LIVE_DEFAULT_VOICE = 'marin';

// 即時互動 2.0 複刻聲（Fish Audio model，id = 32 hex）。
// 完整目錄（中英文名 / 性別 / 試聽 MP3 / 帳號客製音色）由後端 GET /api/ivr-flow/voices 提供
// （ai_backend/config/fishVoices.json + Firestore fishCustomVoices）；這裡只放內建清單當離線 fallback 與顯示名。
export const FISH_BUILTIN_VOICES = [
    { id: 'cd3da9e4ff434929a3e35ff3f690d585', nameZh: '品瑄', nameEn: 'Pinxuan', gender: 'female' },
    { id: '93206474fd574f87aeab3b6c118c031e', nameZh: '書晴', nameEn: 'Shuqing', gender: 'female' },
    { id: '2f86896e24514a0797cc5ca06d86a005', nameZh: '允熙', nameEn: 'Yunxi', gender: 'female' },
    { id: '252f04fd3df845a2ae51ca5cc6e5d8b3', nameZh: '采寧', nameEn: 'Caining', gender: 'female' },
    { id: 'f49f0ae3f7d54c1a906a477461cda87f', nameZh: '小優', nameEn: 'Xiaoyou', gender: 'female' },
    { id: 'a2eb4c0b2d424d6e9571602a00bda76d', nameZh: '曼君', nameEn: 'Manjun', gender: 'female' },
    { id: '993d34de97d84633884a796d831eecd0', nameZh: '麗芳', nameEn: 'Lifang', gender: 'female' },
    { id: '1d39e1c802d240a795ab32fd049c941e', nameZh: '玉霞', nameEn: 'Yuxia', gender: 'female' },
    { id: '51e0f9d31e574127a0f7c16dbec35012', nameZh: '金枝', nameEn: 'Jinzhi', gender: 'female' },
    { id: '421a9594d05e47c785e6cad5444392ad', nameZh: '小恩', nameEn: 'Xiaoen', gender: 'female' },
    { id: '10fda2258d6246ef9b45a10d78fe5e65', nameZh: '芽芽', nameEn: 'Yaya', gender: 'female' },
    { id: 'fb821b8108174ef9b0eb2e4fc48cdb87', nameZh: '睿澤', nameEn: 'Ruize', gender: 'male' },
    { id: '9c12d420fba74761a32d8abe942b83e5', nameZh: '宇翔', nameEn: 'Yuxiang', gender: 'male' },
    { id: '42a2744b97c14e849d44f2053e89a0c0', nameZh: '允誠', nameEn: 'Yuncheng', gender: 'male' },
    { id: '696d05eeeabd48d58ceb97b10a6d648f', nameZh: '承恩', nameEn: 'Chengen', gender: 'male' },
    { id: '7a63f7b1dc7d4fc98e09d7e5c2e15eb4', nameZh: '福來', nameEn: 'Fulai', gender: 'male' },
    { id: '9427760645d8430da098389cca7a991c', nameZh: '小樂', nameEn: 'Xiaole', gender: 'male' },
    { id: '913873969907418eab0a9d680f2f37b0', nameZh: '皮皮', nameEn: 'Pipi', gender: 'male' },
];
export const FISH_VOICES = {
    female: FISH_BUILTIN_VOICES.filter(v => v.gender === 'female').map(v => v.id),
    male: FISH_BUILTIN_VOICES.filter(v => v.gender === 'male').map(v => v.id),
};
export const FISH_DEFAULT_VOICE = 'cd3da9e4ff434929a3e35ff3f690d585';
export const isFishVoiceId = (id) => /^[0-9a-f]{32}$/.test(String(id || ''));

// 顯示名快取：內建先塞，VoicePicker 拿到後端目錄（含客製）後 registerCloneVoices() 補進來
const _fishNames = new Map(FISH_BUILTIN_VOICES.map(v => [v.id, v]));
export function registerCloneVoices(list) {
    for (const v of list || []) if (v?.id) _fishNames.set(v.id, v);
}
/** 「品瑄 · Pinxuan」；找不到就回 id 前 8 碼 */
export function cloneVoiceLabel(id) {
    const v = _fishNames.get(id);
    if (!v) return isFishVoiceId(id) ? String(id).slice(0, 8) : id;
    return v.nameEn && v.nameEn !== v.nameZh ? `${v.nameZh} · ${v.nameEn}` : v.nameZh;
}

// Grok 內建音色（已剔除實測偏大陸腔的 carina / naksh / cosmo）。
// ⚠️ 僅供既有 grok avatar 顯示用，不再開放新選 —— 見檔頭說明。
export const GROK_VOICES = {
    female: ['luna', 'ara', 'eve', 'celeste', 'iris', 'ursa'],
    male: ['helix', 'lumen', 'sal', 'rex', 'rigel', 'sirius', 'orion',
        'altair', 'atlas', 'castor', 'helios', 'kepler', 'leo', 'lux', 'perseus', 'zagan', 'zenith'],
};
export const GROK_DEFAULT_VOICE = 'luna';

// 依 provider 取音色池與預設值（VoicePicker / Wizard 共用，避免兩邊各判一次 provider）
export function voicePoolFor(provider) {
    const p = (provider || 'gemini').toLowerCase();
    if (p === 'openai') return { voices: OPENAI_VOICES, defaultVoice: OPENAI_DEFAULT_VOICE };
    if (p === 'openai-live') return { voices: OPENAI_LIVE_VOICES, defaultVoice: OPENAI_LIVE_DEFAULT_VOICE };
    if (p === 'grok') return { voices: GROK_VOICES, defaultVoice: GROK_DEFAULT_VOICE };
    if (p === 'qwen-omni-tts' || p === 'qwen') return { voices: FISH_VOICES, defaultVoice: FISH_DEFAULT_VOICE };
    return { voices: null, defaultVoice: 'Aoede' };   // null = 用 Gemini 原本的清單
}

function inPool(pool, v) {
    return pool.female.includes(v) || pool.male.includes(v);
}

// 是不是 realtime provider 的音色（小寫 id，沒進 i18n）——決定要不要走顯示名 / 隱藏 hint
export function isRealtimeVoice(id) {
    if (!id) return false;
    const v = String(id).toLowerCase();
    return inPool(OPENAI_VOICES, v) || inPool(OPENAI_LIVE_VOICES, v) || inPool(GROK_VOICES, v) || isFishVoiceId(v);
}

// 舊名保留：外部仍有呼叫點，語意擴大成「realtime 音色」
export const isGrokVoice = isRealtimeVoice;

// 顯示名：realtime 小寫 id → 首字大寫（Shimmer / Luna）；gemini 維持原樣
export function voiceDisplayName(id) {
    if (!id) return id;
    if (isFishVoiceId(id)) return cloneVoiceLabel(id);
    return isRealtimeVoice(id) ? id.charAt(0).toUpperCase() + id.slice(1) : id;
}
